
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Book, MoreVertical, CheckCircle2, Clock, AlertCircle, FileText, User, Loader2, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserKnowledgeBases, useUserFullAccessKnowledgeBases, useLicensedKnowledgeBases } from "@/hooks/useKnowledgeBases";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";

interface KnowledgeBaseListProps {
  userId?: string;
  channelName?: string;
  accessType?: 'created' | 'full';
  onNavigate?: () => void;
}

const KnowledgeBaseList = ({ userId, channelName, accessType = 'created', onNavigate }: KnowledgeBaseListProps = {}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const effectiveUserId = userId || user?.id;
  const isOwnProfile = user?.id === effectiveUserId;
  
  // Query for user-based KBs (created only)
  const { data: userCreatedKBs, isLoading: userCreatedKBsLoading } = useUserKnowledgeBases(
    accessType === 'created' ? effectiveUserId : undefined
  );
  
  // Query for full access KBs (created + attributed + licensed)
  const { data: userFullAccessKBs, isLoading: userFullAccessKBsLoading } = useUserFullAccessKnowledgeBases(
    accessType === 'full' ? effectiveUserId : undefined
  );
  
  // Query for licensed KBs only (accessible but not created by user)
  const { data: licensedKBs, isLoading: licensedKBsLoading } = useLicensedKnowledgeBases(effectiveUserId);
  
  // Select the appropriate data based on accessType
  const userKBs = accessType === 'full' ? userFullAccessKBs : userCreatedKBs;
  const userKBsLoading = accessType === 'full' ? userFullAccessKBsLoading : userCreatedKBsLoading;
  
  // Query for channel-based KBs
  const { data: channelKBs, isLoading: channelKBsLoading } = useQuery({
    queryKey: ['channel-knowledge-bases', channelName],
    queryFn: async () => {
      if (!channelName) return [];
      
      let query = supabase
        .from('knowledge_bases')
        .select(`
          *,
          domains(id, name, symbol),
          subdomains(id, name),
          attributions(
            id,
            ownership_percentage,
            profiles(id, display_name, avatar_url)
          )
        `)
        .eq('status', 'published')
        .is('deleted_at', null);
      
      // For ispai channel, show all published KBs
      // For other channels, filter by channel_name
      if (channelName.toLowerCase() !== 'ispai') {
        query = query.eq('channel_name', channelName.toUpperCase());
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!channelName
  });

  // Determine which data source to use
  const knowledgeBases = channelName ? channelKBs : userKBs;
  const isLoading = channelName ? channelKBsLoading : userKBsLoading;

  if (isLoading) {
    return (
      <Card className="border-2 border-black">
        <CardContent className="p-8 flex justify-center items-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-black">
      <CardHeader className="pb-2 border-b border-gray-200">
        <CardTitle className="text-lg">Knowledge Bases</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-gray-100 rounded-none">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="confirmed">Minted</TabsTrigger>
            <TabsTrigger value="unconfirmed">Draft</TabsTrigger>
            <TabsTrigger value="licensed">Licensed</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="p-0">
            <div className="h-[500px] overflow-y-auto overscroll-contain touch-pan-y" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="p-4 space-y-4">
                {!knowledgeBases || knowledgeBases.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Book className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                    <p>{channelName ? `No knowledge bases in ${channelName.toUpperCase()} channel yet` : 'No knowledge bases yet'}</p>
                    {!channelName && isOwnProfile && (
                      <Button 
                        size="sm" 
                        onClick={() => navigate("/call-for-knowledge")} 
                        className="mt-4 bg-black hover:bg-black/90"
                      >
                        Create Your First Knowledge Base
                      </Button>
                    )}
                  </div>
                ) : (
                  knowledgeBases.map((kb) => (
                    <KnowledgeBaseItem key={kb.id} knowledgeBase={kb} isOwnProfile={isOwnProfile} onNavigate={onNavigate} />
                  ))
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="confirmed" className="p-0">
            <div className="h-[500px] overflow-y-auto overscroll-contain touch-pan-y" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="p-4 space-y-4">
              {knowledgeBases?.filter(kb => kb.nft_cover_image_url).map((kb) => (
                  <KnowledgeBaseItem key={kb.id} knowledgeBase={kb} isOwnProfile={isOwnProfile} onNavigate={onNavigate} />
                ))}
                {(!knowledgeBases || knowledgeBases.filter(kb => kb.nft_cover_image_url).length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <Book className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                    <p>No minted knowledge bases</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="unconfirmed" className="p-0">
            <div className="h-[500px] overflow-y-auto overscroll-contain touch-pan-y" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="p-4 space-y-4">
              {knowledgeBases?.filter(kb => !kb.nft_cover_image_url).map((kb) => (
                  <KnowledgeBaseItem key={kb.id} knowledgeBase={kb} isOwnProfile={isOwnProfile} onNavigate={onNavigate} />
                ))}
                {(!knowledgeBases || knowledgeBases.filter(kb => !kb.nft_cover_image_url).length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <Book className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                    <p>No draft knowledge bases</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="licensed" className="p-0">
            <div className="h-[500px] overflow-y-auto overscroll-contain touch-pan-y" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="p-4 space-y-4">
                {licensedKBsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : licensedKBs && licensedKBs.length > 0 ? (
                  licensedKBs.map((kb) => (
                    <KnowledgeBaseItem key={kb.id} knowledgeBase={kb} isOwnProfile={false} onNavigate={onNavigate} />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Book className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                    <p>No licensed knowledge bases</p>
                    <p className="text-xs mt-1">KBs you can access via channel membership or grants</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

const KnowledgeBaseItem = ({ knowledgeBase, isOwnProfile, onNavigate }: { knowledgeBase: any; isOwnProfile: boolean; onNavigate?: () => void }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // Check if current user is the KB creator - this controls Edit/Mint/Delete actions
  const isKBCreator = user?.id === knowledgeBase.creator_id;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const handleDownload = async () => {
    if (!knowledgeBase.file_url) return;
    
    setIsDownloading(true);
    try {
      // Extract the file path from the URL
      const url = new URL(knowledgeBase.file_url);
      const pathParts = url.pathname.split('/');
      const bucketIndex = pathParts.indexOf('knowledge-bases');
      const filePath = pathParts.slice(bucketIndex + 1).join('/');
      
      // Download using Supabase SDK
      const { data, error } = await supabase.storage
        .from('knowledge-bases')
        .download(filePath);
      
      if (error) throw error;
      
      // Create a blob URL and trigger download
      const blob = new Blob([data], { type: knowledgeBase.file_type });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = knowledgeBase.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast({
        title: "Download Started",
        description: `Downloading ${knowledgeBase.file_name}`,
      });
    } catch (error) {
      // Download error handled silently
      toast({
        title: "Download Failed",
        description: "Unable to download the file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('knowledge_bases')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge_bases'] });
      toast({
        title: "Knowledge Base Deleted",
        description: "The knowledge base has been removed from your list.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to Delete Knowledge Base",
        description: "Try again.",
        variant: "destructive",
      });
      // Delete error handled by mutation
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate(knowledgeBase.id);
    setShowDeleteDialog(false);
  };

  const handleViewDetails = () => {
    onNavigate?.();
    navigate(`/knowledge-base/${knowledgeBase.id}`);
  };

  const handleEdit = () => {
    onNavigate?.();
    navigate(`/edit-knowledge-base/${knowledgeBase.id}`);
  };

  const handleMint = () => {
    toast({
      title: "Mint Knowledge Base",
      description: "Web3 minting capability will be available soon!",
    });
  };

  // Get team members from attributions
  const teamMembers = knowledgeBase.attributions || [];
  
  // Check if current user has download access (creator or attributed team member)
  const hasDownloadAccess = isKBCreator || 
    teamMembers.some((member: any) => member.profiles?.id === user?.id);
  
  return (
    <>
      <div className="p-4 border rounded-lg hover:shadow-sm transition-shadow">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-start space-x-2">
            <Book className="h-5 w-5 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-medium">{knowledgeBase.title}</h3>
              <p className="text-sm text-gray-600 line-clamp-2">{knowledgeBase.description || 'No description'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {knowledgeBase.nft_cover_image_url && (
              <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
                <CheckCircle2 className="mr-1 h-3 w-3" />
                Minted
              </Badge>
            )}
            {!knowledgeBase.nft_cover_image_url && (
              <Badge variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50">
                <Clock className="mr-1 h-3 w-3" />
                Draft
              </Badge>
            )}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={5}>
                <DropdownMenuItem onClick={handleViewDetails}>
                  View Details
                </DropdownMenuItem>
                {isKBCreator && (
                  <>
                    <DropdownMenuItem onClick={handleEdit}>
                      Edit
                    </DropdownMenuItem>
                    {!knowledgeBase.nft_cover_image_url && (
                      <DropdownMenuItem onClick={handleMint}>
                        Mint
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        {/* Team Members Section */}
        {teamMembers.length > 0 && (
          <div className="flex items-center gap-2 mb-3 pb-3 border-b">
            <span className="text-xs font-medium text-gray-500">
              {teamMembers.length === 1 ? 'Creator:' : 'Team:'}
            </span>
            <div className="flex items-center gap-2">
              {teamMembers.map((member: any, index: number) => (
                <div key={index} className="flex items-center gap-1.5">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={member.profiles?.avatar_url} />
                    <AvatarFallback className="text-xs">
                      {member.profiles?.display_name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-gray-700">
                    {member.profiles?.display_name || 'Unknown'}
                  </span>
                  {member.ownership_percentage > 0 && (
                    <span className="text-xs text-gray-400">
                      ({member.ownership_percentage}%)
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-gray-500">
          <div>
            <span className="font-medium">Domain: </span>
            {knowledgeBase.domains?.name || 'General'}
          </div>
          <div>
            <span className="font-medium">Subdomain: </span>
            {knowledgeBase.subdomains?.name || 'N/A'}
          </div>
          <div>
            <span className="font-medium">Created: </span>
            {formatDate(knowledgeBase.created_at)}
          </div>
          <div className="flex items-center min-w-0 overflow-hidden">
            <FileText className="h-3 w-3 mr-1 flex-shrink-0" />
            {knowledgeBase.file_url && hasDownloadAccess ? (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                disabled={isDownloading}
                className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50 truncate min-w-0"
              >
                <span className="truncate">{isDownloading ? 'Downloading...' : knowledgeBase.file_name}</span>
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </button>
            ) : (
              <span className="truncate text-muted-foreground">{knowledgeBase.file_name || 'No file'}</span>
            )}
          </div>
          <div>
            <span className="font-medium">Size: </span>
            {formatFileSize(knowledgeBase.file_size)}
          </div>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this knowledge base?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{knowledgeBase.title}" from your list. The knowledge base will be archived in our system for record-keeping purposes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default KnowledgeBaseList;
